import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'preact/hooks';
import { useAuthStore } from '@xantory/store';
import { api, refreshAccount, request } from '../../core/client';
import { authEndpoints } from './auth.endpoints';
import { userEndpoints } from '../users/users.endpoints';
import type { LoginRequest, LoginResponse, MfaGate, TokenPair } from './auth.types';
import type { LocalUserProfile } from '../users/users.types';

interface JwtClaims {
  sub: string;
  user_type: string;
  roles: string[];
}

function decodeJwtPayload(token: string): Partial<JwtClaims> {
  try {
    const segment = token.split('.')[1] ?? '';
    const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(padded)) as Partial<JwtClaims>;
  } catch {
    return {};
  }
}

/** Establish the session from an issued token pair (shared by password login
 *  and 2FA verification). */
async function establishSession(pair: TokenPair) {
  const { setSession, setUser } = useAuthStore.getState();
  const { access_token, refresh_token } = pair;
  const exp = {
    ...(pair.access_exp ? { accessExp: pair.access_exp } : {}),
    ...(pair.refresh_exp ? { refreshExp: pair.refresh_exp } : {}),
  };

  const claims = decodeJwtPayload(access_token);
  const userType = claims.user_type ?? 'local';
  const roles = claims.roles ?? [];
  // `sub` IS the user id the backend authenticated (`JwtToken::issue` stamps it from
  // `user.id`), and it keys the account map — so it must be the id everywhere, or the
  // map key and `user.id` could disagree and switching would address a phantom.
  const sub = claims.sub ?? '';

  // ⚠ SEED THE ACCOUNT BEFORE FETCHING THE PROFILE. `request()` reads the ACTIVE
  // account's token out of the store, so until this lands the call below is
  // unauthenticated — it 401s and the profile (with it, `permissions`) is silently
  // lost. That failure hides completely behind an admin, who bypasses every
  // permission check anyway, and only shows up as a crippled UI for everyone else.
  setSession(
    access_token,
    refresh_token,
    { id: sub, email: '', firstName: '', lastName: '', userType, roles },
    exp,
  );

  try {
    const profile = await request<LocalUserProfile>(userEndpoints.me);
    setUser({
      id: sub,
      email: profile.email,
      firstName: profile.first_name,
      lastName: profile.last_name ?? '',
      userType,
      roles,
      permissions: profile.permissions ?? {},
      ...(profile.home_site_id ? { homeSiteId: profile.home_site_id } : {}),
    });
  } catch {
    // Keep the claims-only session: the operator is signed in, the profile simply
    // has not landed. `validateSession` retries it on the next boot.
  }
}

/** Password login. When the tier enforces 2FA the resolved value carries an
 *  `mfa` gate instead of a session — the caller drives enrol/verify. */
export function useLogin() {
  return useMutation({
    mutationFn: (req: LoginRequest) => api.post<LoginResponse>(authEndpoints.login, req, { noAuth: true }),
    onSuccess: async (data): Promise<MfaGate | null> => {
      if (data.token) {
        await establishSession(data.token);
        return null;
      }
      return data.mfa ?? null;
    },
  });
}

/** Complete a 2FA challenge (enrolment or login code) → session. */
export function useVerifyMfa() {
  return useMutation({
    mutationFn: (r: { challenge_id: string; code: string }) =>
      api.post<TokenPair>(authEndpoints.verifyMfa, r, { noAuth: true }),
    onSuccess: (pair) => establishSession(pair),
  });
}

/** Request a 2FA reset email (neutral response). */
export function useMfaRecover() {
  return useMutation({
    mutationFn: (email: string) => api.post<string>(authEndpoints.mfaRecover, { email }, { noAuth: true }),
  });
}

/** Confirm the emailed reset token — clears the authenticator for re-enrolment. */
export function useMfaRecoverConfirm() {
  return useMutation({
    mutationFn: (token: string) =>
      api.post<string>(authEndpoints.mfaRecoverConfirm, { token }, { noAuth: true }),
  });
}

/** Step-up: verify a fresh code for a 10-minute elevation window (D-080). */
export function useStepUp() {
  return useMutation({
    mutationFn: (code: string) => api.post<boolean>(authEndpoints.stepUp, { code }),
  });
}

export function useLogout() {
  const clearSession = useAuthStore((s) => s.clearSession);
  return useMutation({
    mutationFn: () => api.post<void>(authEndpoints.logout),
    onSettled: () => clearSession(),
  });
}

/**
 * Validate a session restored from storage on app boot — one authenticated call to the profile
 * endpoint. The client interceptor does the rest: a dead/expired access token triggers a refresh,
 * and if that also fails the session is cleared (so the app bounces to login). Resolves when the
 * session is good; rejects on an unrecoverable auth failure (already logged out) or a transport error.
 */
export async function validateSession(): Promise<void> {
  const profile = await request<LocalUserProfile>(userEndpoints.me);
  const { user, setUser } = useAuthStore.getState();
  if (user) {
    setUser({
      ...user,
      // Deliberately NOT `profile.user_id`: `user.id` is the account-map key, and a
      // re-key here would orphan the very account being revalidated.
      email: profile.email,
      firstName: profile.first_name,
      lastName: profile.last_name ?? '',
      userType: profile.user_type ?? user.userType,
      roles: profile.roles ?? user.roles,
      permissions: profile.permissions ?? {},
      ...(profile.home_site_id ? { homeSiteId: profile.home_site_id } : {}),
    });
  }
}

/**
 * What a switch attempt actually did. Three outcomes, deliberately distinct —
 * "that account's session ran out" and "you are offline" call for different
 * sentences on screen, and collapsing them into one failure is how a
 * transport blip gets reported as an expired login.
 */
export type SwitchOutcome = 'switched' | 'expired' | 'offline' | 'unknown';

/**
 * Switch to another signed-in account (R-303).
 *
 * **Validates BEFORE switching, not after.** Only the active account is kept warm,
 * so a background account may have lapsed while it sat there — and finding that out
 * *after* flipping would leave the operator staring at a console signed in as
 * nobody. Rotating the target first means a failed switch leaves them exactly where
 * they were. `/auth/refresh` is the right probe because it re-reads roles,
 * `home_site_id` and the disabled flag server-side, so a switch can never resume
 * with stale authority.
 *
 * **The cache is cleared on success.** React Query keys carry no account, so without
 * this the previous operator's farm data would render under the new one's session
 * until each query happened to refetch — a different person's view of a live farm,
 * which on safety-critical infra is not a cosmetic bug.
 */
export function useSwitchAccount(): (userId: string) => Promise<SwitchOutcome> {
  const queryClient = useQueryClient();
  return useCallback(
    async (userId: string): Promise<SwitchOutcome> => {
      const { accounts, activeId, switchTo } = useAuthStore.getState();
      if (!accounts[userId]) return 'unknown';
      if (userId === activeId) return 'switched';

      const token = await refreshAccount(userId);
      if (!token) {
        // `refreshAccount` drops the account only when the server actually refused
        // it; a transport failure leaves it in place. That is the difference.
        return useAuthStore.getState().accounts[userId] ? 'offline' : 'expired';
      }

      switchTo(userId);
      queryClient.clear();
      return 'switched';
    },
    [queryClient],
  );
}

/** Sign OUT of one account without leaving the console (the switcher's row action). */
export function useRemoveAccount(): (userId: string) => void {
  const queryClient = useQueryClient();
  return useCallback(
    (userId: string) => {
      const wasActive = useAuthStore.getState().activeId === userId;
      useAuthStore.getState().removeAccount(userId);
      // Same reasoning as the switch: whatever is on screen belonged to the account
      // that just went away, or to the one now promoted in its place.
      if (wasActive) queryClient.clear();
    },
    [queryClient],
  );
}
